/**
 * «Agregar combo o receta» (POS y cotización, spec 20): se elige el combo, se escribe la medida del trabajo (o cuántos
 * combos) y se ve cómo sale cada cantidad antes de agregarlo. También sirve para «Cambiar medida» de un combo del carrito.
 * Las cantidades las calcula el servidor con la receta vigente (la misma función que valida al cobrar).
 */
import { formatearCantidad, Money, TIPO_COMBO_ETIQUETA, UNIDAD_MEDIDA_ETIQUETA, type CalculoComboDTO, type ComboResumenDTO } from '@mg/shared';
import { Layers } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/componentes/ui/table.tsx';
import { mensajeDeError } from '@/lib/api.ts';
import { normalizarDecimal } from '@/lib/montos.ts';
import { calcularCombo, useCombos } from './api.ts';

interface Props {
  abierto: boolean;
  cerrar: () => void;
  /** Devuelve el cálculo listo para poner sus renglones en el carrito. */
  alAgregar: (calculo: CalculoComboDTO) => void;
  /** «Cambiar medida»: el combo ya elegido y su medida actual. */
  inicial?: { combo_id: string; nombre: string; tipo: ComboResumenDTO['tipo']; unidad_medida: ComboResumenDTO['unidad_medida']; medida: string } | null;
  /** Texto del botón para agregar (en la cotización: «Agregar a la cotización»). */
  textoAgregar?: string;
}

export function DialogAgregarCombo({ abierto, cerrar, ...resto }: Props) {
  return (
    <Dialog open={abierto} onOpenChange={(o) => !o && cerrar()}>
      {/* Se monta de nuevo en cada apertura: arranca limpio, o con el combo y la medida de «Cambiar medida» */}
      {abierto ? <ContenidoAgregarCombo cerrar={cerrar} {...resto} /> : null}
    </Dialog>
  );
}

/** Lo que pidió la vista previa y lo que respondió el servidor (para no mostrar el cálculo de otra medida). */
interface Resultado {
  clave: string;
  calculo: CalculoComboDTO | null;
  error: string | null;
}

function ContenidoAgregarCombo({ cerrar, alAgregar, inicial = null, textoAgregar = 'Agregar al carrito' }: Omit<Props, 'abierto'>) {
  const [q, setQ] = useState('');
  const [elegido, setElegido] = useState<Pick<ComboResumenDTO, 'id' | 'nombre' | 'tipo' | 'unidad_medida'> | null>(() => (inicial ? { id: inicial.combo_id, nombre: inicial.nombre, tipo: inicial.tipo, unidad_medida: inicial.unidad_medida } : null));
  const [medida, setMedida] = useState(() => (inicial ? formatearCantidad(Money.parse(inicial.medida), 4) : ''));
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const lista = useCombos({ q: q.trim() || undefined, activo: 'true', limite: 20 }, !inicial);

  // Vista previa: el servidor calcula con la receta vigente (debounce corto). Solo vale el resultado de lo que se ve.
  const medidaNormal = normalizarDecimal(medida);
  const clave = elegido && medidaNormal && Money.parse(medidaNormal).esPositivo() ? `${elegido.id}|${medidaNormal}` : null;
  useEffect(() => {
    if (!clave || !elegido) return;
    let vivo = true;
    const t = setTimeout(() => {
      calcularCombo(elegido.id, medidaNormal, true)
        .then((c) => vivo && setResultado({ clave, calculo: c, error: null }))
        .catch((e) => vivo && setResultado({ clave, calculo: null, error: mensajeDeError(e) }));
    }, 200);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [clave, elegido, medidaNormal]);
  const vigente = clave && resultado?.clave === clave ? resultado : null;
  const calculo = vigente?.calculo ?? null;
  const error = vigente?.error ?? null;
  const calculando = !!clave && !vigente;

  const etiquetaMedida = elegido?.tipo === 'fijo' ? '¿Cuántos combos?' : `Medida del trabajo (${UNIDAD_MEDIDA_ETIQUETA[elegido?.unidad_medida ?? 'm2']})`;

  return (
    <DialogContent className="max-w-3xl" data-testid="dialogo-agregar-combo">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5 text-mg-verde" /> {inicial ? `Cambiar medida · ${inicial.nombre}` : 'Agregar combo o receta'} <AyudaVentana tema="combos.agregar" />
        </DialogTitle>
        <DialogDescription>Los productos entran con sus cantidades y el descuento del combo. Si después quitas o cambias uno, el combo se desarma y pierde el descuento.</DialogDescription>
      </DialogHeader>

      {!elegido ? (
        <div className="space-y-2">
          <Input autoFocus placeholder="Buscar combo o receta por nombre o código" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar combo o receta" data-testid="combo-buscar" />
          <div className="max-h-72 overflow-y-auto rounded-lg border">
            {lista.isLoading ? <p className="p-3 text-sm text-muted-foreground">Buscando…</p> : null}
            {lista.data && lista.data.datos.length === 0 ? <p className="p-3 text-sm text-muted-foreground">No hay combos activos{q ? ' con ese nombre' : ''}. La dueña los crea en Productos › Combos y recetas.</p> : null}
            {(lista.data?.datos ?? []).map((k) => (
              <button
                key={k.id}
                type="button"
                disabled={!k.vendible}
                onClick={() => setElegido(k)}
                className="flex w-full items-center justify-between gap-2 border-b px-3 py-2 text-left text-sm last:border-b-0 hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                data-testid={`combo-opcion-${k.codigo}`}
              >
                <span>
                  <span className="font-medium">{k.nombre}</span> <span className="text-xs text-muted-foreground">{k.codigo} · {k.componentes} producto(s)</span>
                </span>
                <span className="flex items-center gap-1">
                  <Badge variant="neutro">{k.tipo === 'fijo' ? 'Combo' : `Receta por ${UNIDAD_MEDIDA_ETIQUETA[k.unidad_medida ?? 'm2']}`}</Badge>
                  {Money.parse(k.descuento_pct).esPositivo() ? <Badge variant="ok">−{Money.parse(k.descuento_pct).toDisplay()} %</Badge> : null}
                  {!k.vendible ? <Badge variant="critico">Producto desactivado</Badge> : null}
                </span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground">{TIPO_COMBO_ETIQUETA[elegido.tipo]}</p>
              <p className="font-medium" data-testid="combo-elegido">{elegido.nombre}</p>
            </div>
            <Campo etiqueta={etiquetaMedida}>
              <Input autoFocus inputMode="decimal" value={medida} onChange={(e) => setMedida(e.target.value)} className="w-40 text-right" data-testid="combo-medida" />
            </Campo>
            {!inicial ? (
              <Button variant="ghost" onClick={() => setElegido(null)}>
                Elegir otro
              </Button>
            ) : null}
          </div>
          {error ? <p className="rounded-lg bg-red-50 px-3 py-1.5 text-sm text-red-800" data-testid="combo-error">{error}</p> : null}
          {calculo ? (
            <Table data-testid="combo-vista-previa">
              <TableHeader>
                <TableRow>
                  <TableHead>Producto</TableHead>
                  <TableHead>Cómo sale la cantidad</TableHead>
                  <TableHead className="text-right">Cantidad</TableHead>
                  <TableHead className="text-right">Desc. combo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {calculo.renglones.map((r) => (
                  <TableRow key={r.presentacion_id} data-testid={`combo-renglon-${r.producto_codigo}`}>
                    <TableCell>
                      <span className="font-medium">{r.producto_nombre}</span> <span className="text-xs text-muted-foreground">{r.producto_codigo}</span>
                      {r.empaque ? <Badge variant="neutro" className="ml-1">empaque</Badge> : null}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.explicacion}</TableCell>
                    <TableCell className="text-right num font-medium">{formatearCantidad(Money.parse(r.cantidad), 2)}</TableCell>
                    <TableCell className="text-right num">{Money.parse(r.descuento_pct).esPositivo() ? `−${Money.parse(r.descuento_pct).toDisplay()} %` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : null}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={cerrar}>
          Cancelar
        </Button>
        <Button disabled={!calculo || calculando} onClick={() => calculo && alAgregar(calculo)} data-testid="combo-agregar">
          {inicial ? 'Cambiar medida' : textoAgregar}
        </Button>
      </div>
    </DialogContent>
  );
}
